import { getOpenAPIMeta } from '@orpc/openapi'

const RESERVED_EXACT = new Set([
  '/api/health',
  '/api/openapi.json',
  '/api/realtime',
  '/api/rpc',
  '/api/auth',
  '/api/files',
])
const RESERVED_PREFIXES = ['/api/rpc/', '/api/auth/', '/api/files/']

function collisionForBunderstackPath(path: string): string | undefined {
  if (RESERVED_EXACT.has(path)) return 'it is reserved by bunderstack'
  const prefix = RESERVED_PREFIXES.find((value) => path.startsWith(value))
  return prefix ? `"${prefix}*" is reserved by bunderstack` : undefined
}

export interface ApiRegistryEntry {
  handle: string
  operationId: string
  method: string
  path: string
  source: string
}

export interface ApiRegistry {
  entries: ApiRegistryEntry[]
}

export interface ForeignSpecEntry {
  spec: Record<string, unknown>
  prefix?: string
  source?: string
}

export interface BuildApiRegistryOptions {
  nativeRouter?: Record<string, unknown>
  foreignSpecs?: Array<Record<string, unknown> | ForeignSpecEntry>
  /** Native procedures generated by Bunderstack itself may own reserved paths. */
  reservedCoreHandles?: ReadonlySet<string>
}

const HTTP_METHODS = new Set([
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'HEAD',
  'OPTIONS',
])

export function normalizeApiPath(path: string, prefix?: string): string {
  let cleanPath = path.startsWith('/') ? path : '/' + path
  if (prefix) {
    let cleanPrefix = prefix.startsWith('/') ? prefix : '/' + prefix
    if (cleanPrefix.endsWith('/')) {
      cleanPrefix = cleanPrefix.slice(0, -1)
    }
    if (cleanPath === cleanPrefix || cleanPath.startsWith(cleanPrefix + '/')) {
      // Path already starts with prefix
    } else {
      cleanPath = cleanPrefix + cleanPath
    }
  }
  return cleanPath.replace(/\/+/g, '/')
}

export interface ForeignSpecOptions {
  prefix?: string
  source?: string
}

export function normalizeForeignOpenAPISpec(
  spec: Record<string, unknown>,
  options: ForeignSpecOptions = {},
): Record<string, unknown> {
  const prefix = options.prefix
  const source = options.source
  const cloned: Record<string, unknown> = JSON.parse(JSON.stringify(spec))

  if (cloned.paths && typeof cloned.paths === 'object') {
    const normalizedPaths: Record<string, unknown> = {}
    for (const [routePath, pathItem] of Object.entries(
      cloned.paths as Record<string, unknown>,
    )) {
      const canonicalPath = normalizeApiPath(routePath, prefix)
      normalizedPaths[canonicalPath] = pathItem
    }
    cloned.paths = normalizedPaths
  }

  if (source) {
    cloned['x-bunderstack-source'] = source
  }

  return cloned
}

export function mergeApiRoutersStrict(
  target: Record<string, unknown>,
  source?: Record<string, unknown>,
  prefix: string[] = [],
): Record<string, unknown> {
  if (!source) return { ...target }
  const result: Record<string, unknown> = { ...target }

  for (const [key, val] of Object.entries(source)) {
    const currentPath = [...prefix, key]
    const handle = currentPath.join('.')
    const targetVal = result[key]

    if (targetVal !== undefined) {
      const isTargetNamespace =
        typeof targetVal === 'object' &&
        targetVal !== null &&
        !('~orpc' in targetVal)

      const isSourceNamespace =
        typeof val === 'object' && val !== null && !('~orpc' in val)

      if (isTargetNamespace && isSourceNamespace) {
        result[key] = mergeApiRoutersStrict(
          targetVal as Record<string, unknown>,
          val as Record<string, unknown>,
          currentPath,
        )
      } else {
        throw new Error(
          `[bunderstack] Router merge collision at handle "${handle}": duplicate procedure or namespace collision`,
        )
      }
    } else {
      result[key] = val
    }
  }

  return result
}

function normalizePathSegments(path: string): string[] {
  return path.split('/').filter(Boolean)
}

function isParamSegment(segment: string): boolean {
  return (
    (segment.startsWith('{') && segment.endsWith('}')) ||
    segment.startsWith(':')
  )
}

function normalizePathForCollision(path: string): string {
  const segments = normalizePathSegments(path)
  const normalized = segments.map((seg) =>
    isParamSegment(seg) ? '{param}' : seg,
  )
  return '/' + normalized.join('/')
}

function walkNativeRouter(
  obj: Record<string, unknown>,
  pathSegments: string[] = [],
): ApiRegistryEntry[] {
  const entries: ApiRegistryEntry[] = []

  for (const [key, value] of Object.entries(obj)) {
    if (!value || typeof value !== 'object') continue
    const currentSegments = [...pathSegments, key]

    if ('~orpc' in value) {
      const meta = (getOpenAPIMeta(value as any) || {}) as Record<
        string,
        unknown
      >
      const handle = currentSegments.join('.')
      const method = ((meta.method as string) || 'GET').toUpperCase()
      const routePath = (meta.path as string) || '/' + currentSegments.join('/')
      const operationId =
        typeof meta.operationId === 'string' ? meta.operationId : handle

      entries.push({
        handle,
        operationId,
        method,
        path: routePath,
        source: 'native',
      })
    } else {
      entries.push(
        ...walkNativeRouter(value as Record<string, unknown>, currentSegments),
      )
    }
  }

  return entries
}

function extractForeignEntries(
  specs: Array<Record<string, unknown> | ForeignSpecEntry>,
): ApiRegistryEntry[] {
  const entries: ApiRegistryEntry[] = []

  for (const item of specs) {
    if (!item) continue
    let specObj: Record<string, unknown>
    let source = 'foreign'

    if ('spec' in item && typeof item.spec === 'object' && item.spec !== null) {
      const entry = item as ForeignSpecEntry
      source = entry.source || 'foreign'
      specObj = normalizeForeignOpenAPISpec(entry.spec, {
        prefix: entry.prefix,
        source,
      })
    } else {
      const rawSpec = item as Record<string, unknown>
      source = (rawSpec['x-bunderstack-source'] as string) || 'foreign'
      specObj = normalizeForeignOpenAPISpec(rawSpec, { source })
    }

    if (!specObj || typeof specObj.paths !== 'object' || !specObj.paths)
      continue
    const paths = specObj.paths as Record<string, Record<string, unknown>>

    for (const [routePath, pathItem] of Object.entries(paths)) {
      if (!pathItem || typeof pathItem !== 'object') continue

      for (const [methodKey, operation] of Object.entries(pathItem)) {
        const uppercaseMethod = methodKey.toUpperCase()
        if (!HTTP_METHODS.has(uppercaseMethod)) continue
        if (!operation || typeof operation !== 'object') continue

        const op = operation as Record<string, unknown>
        const handle = `foreign:${source}:${uppercaseMethod}:${routePath}`
        const operationId =
          typeof op.operationId === 'string' ? op.operationId : handle

        entries.push({
          handle,
          operationId,
          method: uppercaseMethod,
          path: routePath,
          source,
        })
      }
    }
  }

  return entries
}

export async function buildApiRegistry(
  options: BuildApiRegistryOptions = {},
): Promise<ApiRegistry> {
  const entries: ApiRegistryEntry[] = []

  if (options.nativeRouter) {
    entries.push(...walkNativeRouter(options.nativeRouter))
  }

  if (options.foreignSpecs) {
    entries.push(...extractForeignEntries(options.foreignSpecs))
  }

  const errors: string[] = []

  for (const entry of entries) {
    if (entry.source !== 'native') continue
    if (options.reservedCoreHandles?.has(entry.handle)) continue
    const reason = collisionForBunderstackPath(entry.path)
    if (reason) {
      errors.push(
        `Reserved route collision on ${entry.method} ${entry.path}: native handle "${entry.handle}" conflicts because ${reason}`,
      )
    }
  }

  // Check duplicate handles
  const handleMap = new Map<string, ApiRegistryEntry>()
  for (const entry of entries) {
    const existing = handleMap.get(entry.handle)
    if (existing) {
      errors.push(
        `Duplicate handle "${entry.handle}": collision between ${existing.source} (${existing.method} ${existing.path}) and ${entry.source} (${entry.method} ${entry.path})`,
      )
    } else {
      handleMap.set(entry.handle, entry)
    }
  }

  // Check duplicate operation IDs
  const opIdMap = new Map<string, ApiRegistryEntry>()
  for (const entry of entries) {
    const existing = opIdMap.get(entry.operationId)
    if (existing) {
      errors.push(
        `Duplicate operation ID "${entry.operationId}": collision between ${existing.source} (${existing.handle}) and ${entry.source} (${entry.handle})`,
      )
    } else {
      opIdMap.set(entry.operationId, entry)
    }
  }

  // Check method/path collisions (exact and parameter ambiguity)
  const routePatternMap = new Map<string, ApiRegistryEntry>()
  for (const entry of entries) {
    const normalizedPath = normalizePathForCollision(entry.path)
    const routeKey = `${entry.method} ${normalizedPath}`

    const existing = routePatternMap.get(routeKey)
    if (existing) {
      if (existing.path === entry.path) {
        errors.push(
          `Exact method/path collision on ${entry.method} ${entry.path}: collision between ${existing.source} (${existing.handle}) and ${entry.source} (${entry.handle})`,
        )
      } else {
        errors.push(
          `Ambiguous parameter path collision on ${entry.method} (${existing.path} vs ${entry.path}): collision between ${existing.source} (${existing.handle}) and ${entry.source} (${entry.handle})`,
        )
      }
    } else {
      routePatternMap.set(routeKey, entry)
    }
  }

  if (errors.length > 0) {
    throw new Error(
      `[bunderstack] Route registry validation failed (${errors.length} error(s)):\n` +
        errors.join('\n'),
    )
  }

  return { entries }
}
